import { type FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import {
  type BukConfig,
  type BukCredentialsResponse,
  type ConnectBukArgs
} from 'src/modules/Settings/Integrations/pages/components/integrations2.0/Buk/utils';
import { zeusApiV1 } from 'src/store/api';

export type BukCompany = {
  id: number;
  name: string;
  rut?: string;
  ruc?: string;
};

type BukParamsResponse = {
  companies: BukCompany[];
};

const extendedApi = zeusApiV1.injectEndpoints({
  endpoints: build => ({
    saveBuk: build.mutation<BukCredentialsResponse, Partial<BukConfig>>({
      query: args => ({
        url: '/hercules/v1/buk/config',
        method: 'PATCH',
        body: args,
        params: { subsidiary: args.subsidiary }
      }),
      invalidatesTags: ['integration']
    }),
    connectBuk: build.mutation<BukCredentialsResponse, ConnectBukArgs>({
      queryFn: async ({ subsidiary, body }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            url: '/hercules/v1/buk/credentials',
            method: 'PUT',
            params: { subsidiary },
            body
          });

          if (result.error) {
            return {
              error: {
                ...result.error,
                data: {
                  error: 'integrations.invalid credentials'
                }
              } as unknown as FetchBaseQueryError
            };
          }

          return { data: result.data as BukCredentialsResponse };
        } catch (error) {
          console.error('Error connecting Buk:', error);

          return { error: error as FetchBaseQueryError };
        }
      },
      invalidatesTags: ['integration', 'buk']
    }),
    getBukParams: build.query<BukParamsResponse, { subsidiary: string }>({
      async queryFn({ subsidiary }, _api, _extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: '/hercules/v1/buk/company-params',
            params: { subsidiary }
          });

          if (result.error) {
            return {
              error: {
                ...result.error,
                data: {
                  error: 'integrations.invalid credentials'
                }
              } as unknown as FetchBaseQueryError
            };
          }

          return { data: result.data as BukParamsResponse };
        } catch (error) {
          console.error('Error fetching Buk params:', error);

          return { error: error as FetchBaseQueryError };
        }
      },
      providesTags: ['buk']
    })
  }),
  overrideExisting: false
});

export const { useSaveBukMutation, useConnectBukMutation, useGetBukParamsQuery } = extendedApi;
